<script setup lang="ts">
import { onMounted } from 'vue'
import type { SearchResult } from '../../types/search'
import KeywordSearchToolbar from './KeywordSearchToolbar.vue'
import KeywordSearchResults from './KeywordSearchResults.vue'
import { useKeywordSearch } from '../../composables/useKeywordSearch'
import { useBibleReading } from '../../composables/useBibleReading'
import { useFrameStore } from '../../stores/frameStore'

interface Props {
  frameId?: number
}

const props = withDefaults(defineProps<Props>(), {
  frameId: 0,
})

const frameStore = useFrameStore()
const { versions, loadVersions } = useBibleReading()
const {
  selectedVersion,
  results,
  totalCount,
  isLoading,
  isLoadingMore,
  error,
  allResultsLoaded,
  searchHistory,
  searchQuery,
  isRegex,
  selectVersion,
  search,
  loadMore,
  clearHistory,
} = useKeywordSearch()

onMounted(async () => {
  await loadVersions()
})

const handleVersionChange = (versionCode: string): void => {
  selectVersion(versionCode)
}

const handleSearch = async (query: string, versionCode: string): Promise<void> => {
  await search(query, versionCode, false)
}

const handleLoadMore = async (): Promise<void> => {
  await loadMore()
}

const handleHistorySelect = (): void => {
  // 搜尋已由 KeywordSearchToolbar 處理
}

const handleClearHistory = (): void => {
  if (selectedVersion.value) {
    clearHistory(selectedVersion.value)
  }
}

const handleResultSelect = (result: SearchResult): void => {
  if (!result.bookId || !selectedVersion.value) return

  const sourceFrame = frameStore.frames.find(f => f.frameId === props.frameId)
  const targetX = sourceFrame ? Math.min(sourceFrame.x + sourceFrame.w + 40, 4000 - 600) : 600
  const targetY = sourceFrame ? sourceFrame.y : 60
  const targetW = sourceFrame ? sourceFrame.w : 600
  const targetH = sourceFrame ? sourceFrame.h : 500
  const bookLabel = result.bookName || result.book || ''

  frameStore.addFrame({
    frameId: Date.now(),
    x: targetX,
    y: targetY,
    w: targetW,
    h: targetH,
    title: `聖經閱讀 - ${bookLabel}${result.chapter}章`,
    type: 'bible-reading' as const,
    color: sourceFrame?.color ?? ('cyan' as const),
    _bibleInfo: {
      versionCode: selectedVersion.value,
      bookId: result.bookId,
      chapter: result.chapter,
    },
  } as any)
}
</script>

<template>
  <div class="keyword-search-frame">
    <KeywordSearchToolbar
      :selected-version="selectedVersion ?? undefined"
      :is-search-enabled="true"
      :is-loading="isLoading"
      :versions="versions"
      :search-history="searchHistory"
      @version-change="handleVersionChange"
      @search="handleSearch"
      @history-select="handleHistorySelect"
      @clear-history="handleClearHistory"
    />

    <KeywordSearchResults
      :results="results"
      :total-count="totalCount"
      :is-loading="isLoading"
      :is-loading-more="isLoadingMore ?? false"
      :all-results-loaded="allResultsLoaded ?? false"
      :error="error ?? undefined"
      :selected-version="selectedVersion ?? undefined"
      :search-query="searchQuery ?? undefined"
      :is-regex="isRegex ?? false"
      @result-select="handleResultSelect"
      @load-more="handleLoadMore"
    />
  </div>
</template>

<style scoped>
.keyword-search-frame {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background-color: white;
}
</style>
